package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.widgets.MenuRowCard
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Palette
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.ThemeAccent
import com.evoapps.evoshift.domain.model.ThemeBase
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.Palettes
import com.evoapps.evoshift.ui.adaptive.cardClickable
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.AppearanceViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.LocalOnColours
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.appearance_accent
import evoshift.composeapp.generated.resources.appearance_accent_bronze
import evoshift.composeapp.generated.resources.appearance_accent_gold
import evoshift.composeapp.generated.resources.appearance_accent_silver
import evoshift.composeapp.generated.resources.appearance_apply
import evoshift.composeapp.generated.resources.appearance_base
import evoshift.composeapp.generated.resources.appearance_base_bottle_green
import evoshift.composeapp.generated.resources.appearance_base_burgundy
import evoshift.composeapp.generated.resources.appearance_base_navy
import evoshift.composeapp.generated.resources.appearance_current
import evoshift.composeapp.generated.resources.appearance_hint
import evoshift.composeapp.generated.resources.appearance_preview
import evoshift.composeapp.generated.resources.appearance_title
import evoshift.composeapp.generated.resources.dashboard_clock_in
import evoshift.composeapp.generated.resources.dashboard_next_shift
import evoshift.composeapp.generated.resources.nav_schedule
import evoshift.composeapp.generated.resources.schedule_open_shift
import org.jetbrains.compose.resources.stringResource

/** Test tags of the appearance picker (SH-196). */
object AppearanceTags {
    const val CARD = "appearance_card"
    const val PREVIEW = "appearance_preview"
    const val PREVIEW_BAR = "appearance_preview_bar"
    fun base(base: ThemeBase) = "appearance_base_${base.name}"
    fun accent(accent: ThemeAccent) = "appearance_accent_${accent.name}"
}

@Composable
fun baseName(base: ThemeBase): String = stringResource(
    when (base) {
        ThemeBase.NAVY -> Res.string.appearance_base_navy
        ThemeBase.BOTTLE_GREEN -> Res.string.appearance_base_bottle_green
        ThemeBase.BURGUNDY -> Res.string.appearance_base_burgundy
    }
)

@Composable
fun accentName(accent: ThemeAccent): String = stringResource(
    when (accent) {
        ThemeAccent.GOLD -> Res.string.appearance_accent_gold
        ThemeAccent.BRONZE -> Res.string.appearance_accent_bronze
        ThemeAccent.SILVER -> Res.string.appearance_accent_silver
    }
)

/** "Burgundy with silver". */
@Composable
fun appearanceLabel(appearance: Appearance): String =
    stringResource(Res.string.appearance_current, baseName(appearance.base), accentName(appearance.accent).lowercase())

/** Profile: "Appearance" with the colours on show; opens [AppearanceDialog]. */
@Composable
fun AppearanceCard(viewModel: AppearanceViewModel, onMessage: suspend (String) -> Unit) {
    val state by viewModel.state.collectAsState()
    // Saveable: applying composes the whole app again (ProvideAppLanguage).
    var open by rememberSaveable { mutableStateOf(false) }
    val error = state.error?.asString()
    LaunchedEffect(error) {
        error?.let {
            onMessage(it)
            viewModel.messageShown()
        }
    }
    MenuRowCard(
        Icons.Default.Palette,
        stringResource(Res.string.appearance_title),
        onClick = {
            viewModel.startPreview()
            open = true
        },
        modifier = Modifier.testTag(AppearanceTags.CARD),
        subtitle = appearanceLabel(state.applied)
    )
    if (open) AppearanceDialog(viewModel, onDismiss = { open = false })
}

/** The base and the accent, each a radio group with a swatch, and a preview of the combination. */
@Composable
fun AppearanceDialog(viewModel: AppearanceViewModel, onDismiss: () -> Unit) {
    val state by viewModel.state.collectAsState()
    EvoDialog(
        title = stringResource(Res.string.appearance_title),
        icon = Icons.Default.Palette,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(stringResource(Res.string.appearance_apply), onClick = {
            onDismiss()
            viewModel.apply()
        })
    ) {
        val dialogOn = LocalOnColours.current
        Text(stringResource(Res.string.appearance_hint), color = dialogOn.onSurfaceVariant)
        AppearancePreview(state.preview)
        OptionGroup(stringResource(Res.string.appearance_base)) {
            viewModel.bases.forEach { base ->
                Option(
                    label = baseName(base),
                    swatch = Palettes.base(base).base,
                    selected = state.preview.base == base,
                    tag = AppearanceTags.base(base),
                    onClick = { viewModel.previewBase(base) }
                )
            }
        }
        OptionGroup(stringResource(Res.string.appearance_accent)) {
            viewModel.accents.forEach { accent ->
                Option(
                    label = accentName(accent),
                    swatch = Palettes.accent(accent).metallic,
                    selected = state.preview.accent == accent,
                    tag = AppearanceTags.accent(accent),
                    onClick = { viewModel.previewAccent(accent) }
                )
            }
        }
    }
}

@Composable
private fun OptionGroup(title: String, options: @Composable () -> Unit) {
    val on = LocalOnColours.current
    Column(Modifier.selectableGroup(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(title, color = on.accent, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
        options()
    }
}

@Composable
private fun Option(label: String, swatch: Color, selected: Boolean, tag: String, onClick: () -> Unit) {
    val on = LocalOnColours.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .testTag(tag)
            .selectable(selected = selected, role = Role.RadioButton, onClick = onClick)
            .padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        RadioButton(
            selected = selected,
            onClick = null,
            colors = RadioButtonDefaults.colors(selectedColor = on.accent, unselectedColor = on.onSurface.copy(alpha = 0.7f))
        )
        Spacer(Modifier.width(12.dp))
        Box(Modifier.size(20.dp).background(swatch, CircleShape).border(1.dp, on.onSurface.copy(alpha = 0.6f), CircleShape))
        Spacer(Modifier.width(12.dp))
        Text(label, color = on.onSurface)
    }
}

/**
 * The combination at a glance, in its own colours (not the ones on show): a top bar, a dark card,
 * a primary button and a chip on the cream screen.
 */
@Composable
fun AppearancePreview(appearance: Appearance, modifier: Modifier = Modifier) {
    val p = Palettes.of(appearance)
    val shape = RoundedCornerShape(12.dp)
    Column(
        modifier
            .fillMaxWidth()
            .testTag(AppearanceTags.PREVIEW)
            .background(Cream, shape)
            .border(1.dp, Color.White.copy(alpha = 0.3f), shape)
            .padding(bottom = 12.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text(
            stringResource(Res.string.appearance_preview),
            modifier = Modifier.padding(start = 12.dp, top = 8.dp),
            style = MaterialTheme.typography.labelMedium,
            color = p.base.onLightVariant
        )
        // The top bar.
        Row(
            Modifier.fillMaxWidth().height(44.dp).background(p.base.base).testTag(AppearanceTags.PREVIEW_BAR).padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.Schedule, contentDescription = null, tint = p.accent.bright, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.nav_schedule), color = Color.White, fontWeight = FontWeight.Bold)
        }
        // A dark card.
        Column(
            Modifier
                .padding(horizontal = 12.dp)
                .fillMaxWidth()
                .background(Brush.linearGradient(listOf(p.base.card, p.base.cardHighlight, p.base.card)), shape)
                .border(1.dp, Brush.linearGradient(listOf(p.accent.metallic, p.accent.bright, p.accent.metallic)), shape)
                .padding(12.dp)
        ) {
            Text(stringResource(Res.string.dashboard_next_shift), color = p.accent.bright, style = MaterialTheme.typography.labelLarge)
            Text("8:00–16:00", color = Color.White, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
        }
        Row(
            Modifier.padding(horizontal = 12.dp).fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // A primary button.
            Row(
                Modifier
                    .background(Brush.linearGradient(listOf(p.accent.metallic, p.accent.bright, p.accent.metallic)), RoundedCornerShape(12.dp))
                    .padding(horizontal = 14.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Default.PlayArrow, contentDescription = null, tint = p.base.base, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text(stringResource(Res.string.dashboard_clock_in), color = p.base.base, fontWeight = FontWeight.Bold)
            }
            // A chip.
            Box(
                Modifier
                    .border(BorderStroke(1.dp, p.accent.deep), RoundedCornerShape(8.dp))
                    .background(p.accent.tint, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 6.dp)
            ) {
                Text(stringResource(Res.string.schedule_open_shift), color = p.accent.deep, style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}
